import * as XLSX from "xlsx";

export interface ValidatorConfig {
  /** default: 'auto' => validate all sheets */
  sheetSelector?: "auto" | string[];

  /** default: true */
  ignoreEmptySheets?: boolean;

  /** Remove fully empty rows when generating cleaned workbook/AoA. default: true */
  deleteEmptyRows?: boolean;

  /** Return cleaned workbook as ArrayBuffer. default: false */
  returnCleanedWorkbook?: boolean;

  /** Stop validation at first Total row after Clients row. default: false */
  stopAtTotalRow?: boolean;

  /** Include the Total row when stopAtTotalRow = true. default: true */
  includeTotalRow?: boolean;

  /** Validate rows after Total as well (for sections like "Offshore Services"). default: true */
  validateAfterTotal?: boolean;

  /** Label in column A that marks the clients header row. default: 'Clients' */
  clientsLabel?: string;

  /** Label in column A that marks total rows. default: 'Total' */
  totalLabel?: string;

  /** Skip validating columns whose metric header matches these labels (case-insensitive). default: [] */
  skipMetricLabels?: string[];
}

export interface ValidationIssue {
  sheet: string;
  client: string;
  period: string;            // e.g. 2025-05-31 or 'YTD-26'
  metric: string;            // e.g. 'Rev'
  columnDisplayName: string; // e.g. '2025-05-31 | Rev'
  fte: string;               // FTE B for the same block
  cellAddress: string;       // e.g. 'R144'
  rowNumber: number;         // 1-based
  colNumber: number;         // 1-based
  message: string;
}

export interface ValidationResult {
  errors: string[];
  issues: ValidationIssue[];

  /** Table output (AoA): header row + data rows */
  issueTable: any[][];

  /** Optional cleaned workbook */
  cleanedWorkbook?: ArrayBuffer;

  /** Optional cleaned AoA per sheet */
  cleanedAoABySheet?: Record<string, any[][]>;
}

/**
 * Validates ALL used columns for ALL data rows across the entire GM workbook.
 * - Finds the metric header row (contains 'FTE B' and 'Save / (Loss)')
 * - Finds the 'Clients' row (column A == clientsLabel)
 * - Validates from the first data row after Clients to end of sheet
 *   (or optionally stop at Total)
 * - DOES include rows after Total by default (to cover Offshore sections)
 * - Produces a table: Client | FTE | Missing Column | Sheet | Period | Cell
 */
export async function validateWorkbookAllColumns(
  file: File,
  config?: ValidatorConfig
): Promise<ValidationResult> {
  const cfg = {
    sheetSelector: config?.sheetSelector ?? "auto",
    ignoreEmptySheets: config?.ignoreEmptySheets ?? true,
    deleteEmptyRows: config?.deleteEmptyRows ?? true,
    returnCleanedWorkbook: config?.returnCleanedWorkbook ?? false,

    // IMPORTANT: default false so we do not stop at the first Total row
    stopAtTotalRow: config?.stopAtTotalRow ?? false,

    includeTotalRow: config?.includeTotalRow ?? true,

    // By default, even if stopAtTotalRow is true, keep validating after Total.
    validateAfterTotal: config?.validateAfterTotal ?? true,

    clientsLabel: config?.clientsLabel ?? "Clients",
    totalLabel: config?.totalLabel ?? "Total",
    skipMetricLabels: (config?.skipMetricLabels ?? []).map(s => s.trim().toLowerCase()),
  };

  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: true });

  const targetSheets = cfg.sheetSelector === "auto"
    ? wb.SheetNames
    : (cfg.sheetSelector ?? wb.SheetNames);

  const errors: string[] = [];
  const issues: ValidationIssue[] = [];
  const cleanedAoABySheet: Record<string, any[][]> = {};

  for (const sheetName of targetSheets) {
    const ws = wb.Sheets[sheetName];
    if (!ws) {
      if (!cfg.ignoreEmptySheets) errors.push(`Sheet "${sheetName}" not found.`);
      continue;
    }

    const range = ws["!ref"] ? XLSX.utils.decode_range(ws["!ref"] as string) : null;
    if (!range) {
      if (!cfg.ignoreEmptySheets) errors.push(`Sheet "${sheetName}" has no used range.`);
      continue;
    }

    const aoaOriginal = XLSX.utils.sheet_to_json<any[]>(ws, {
      header: 1,
      defval: null,
      blankrows: true,
    });

    if (cfg.ignoreEmptySheets && isSheetCompletelyEmpty(aoaOriginal)) {
      cleanedAoABySheet[sheetName] = aoaOriginal;
      continue;
    }

    const headerIdx = findMetricHeaderRowIndex(aoaOriginal);
    const clientsIdx = findRowIndexByFirstCell(aoaOriginal, cfg.clientsLabel);

    if (headerIdx < 0 || clientsIdx < 0) {
      if (!cfg.ignoreEmptySheets) {
        errors.push(`Sheet "${sheetName}": metric header row or Clients row not found.`);
      }
      cleanedAoABySheet[sheetName] = aoaOriginal;
      continue;
    }

    // Header + period rows
    const headerRow = aoaOriginal[headerIdx] ?? [];
    const periodRowIndex = headerIdx - 1; // row above metric headers

    // Determine max column to validate from header row (not !ref)
    const usedMaxCol0 = inferUsedMaxColFromHeader(headerRow, range.e.c);

    // Blocks start at every 'FTE B'
    const blockStarts = findBlockStarts(headerRow, "FTE B");

    // Column metadata used for messages
    const colMeta = buildColumnMeta({
      aoa: aoaOriginal,
      headerIdx,
      periodRowIndex,
      usedMaxCol0Based: usedMaxCol0,
      blockStarts,
      skipMetricLabels: cfg.skipMetricLabels,
    });

    // Validation row range
    const startRow = clientsIdx + 1;

    let firstTotalIdx = -1;
    if (cfg.stopAtTotalRow) {
      firstTotalIdx = findRowIndexByFirstCellAfter(aoaOriginal, cfg.totalLabel, startRow);
    }

    // If stopAtTotalRow=true and validateAfterTotal=false => end at Total (optionally included)
    // Else validate entire sheet
    const endRow = (cfg.stopAtTotalRow && !cfg.validateAfterTotal && firstTotalIdx >= 0)
      ? (cfg.includeTotalRow ? firstTotalIdx : firstTotalIdx - 1)
      : (aoaOriginal.length - 1);

    for (let r = startRow; r <= endRow && r < aoaOriginal.length; r++) {
      const row = aoaOriginal[r] ?? [];

      // skip completely empty rows
      if (isRowCompletelyEmpty(row, usedMaxCol0)) continue;

      const first = toStr(row[0]);

      // skip repeated Clients header rows if present later
      if (first === cfg.clientsLabel) continue;

      // skip pure section-title rows like "Offshore Services" (only column A has text)
      if (first && isOnlyFirstCellFilled(row, usedMaxCol0)) continue;

      // If client name missing but row has other values, add a client-name issue
      if (!first && rowHasAnyValue(row, usedMaxCol0)) {
        const addr = XLSX.utils.encode_cell({ r, c: 0 });
        const issue: ValidationIssue = {
          sheet: sheetName,
          client: `(row ${r + 1})`,
          period: "",
          metric: "Client",
          columnDisplayName: "Client",
          fte: "",
          cellAddress: addr,
          rowNumber: r + 1,
          colNumber: 1,
          message: `Missing client name at ${addr}.`,
        };
        issues.push(issue);
        errors.push(`Sheet "${sheetName}": ${issue.message}`);
      }

      const clientDisplay = first || `(row ${r + 1})`;

      // Validate columns 1..max (skip c=0 here; handled above)
      for (let c = 1; c <= usedMaxCol0; c++) {
        const meta = colMeta[c];
        if (meta?.skip) continue;

        const val = row?.[c] ?? null;
        if (isEmpty(val)) {
          const cellAddress = XLSX.utils.encode_cell({ r, c });
          const period = meta?.period ?? "";
          const metric = meta?.metric ?? `Column ${columnLabel(c)}`;
          const columnDisplayName = meta?.display ?? metric;
          const fte = computeFteForColumn(row, blockStarts, c);

          const issue: ValidationIssue = {
            sheet: sheetName,
            client: clientDisplay,
            period,
            metric,
            columnDisplayName,
            fte,
            cellAddress,
            rowNumber: r + 1,
            colNumber: c + 1,
            message: `Client "${clientDisplay}" missing "${columnDisplayName}" at ${cellAddress}.`,
          };

          issues.push(issue);
          errors.push(`Sheet "${sheetName}": ${issue.message}`);
        }
      }
    }

    // Prepare cleaned AoA (optional)
    if (cfg.deleteEmptyRows) {
      const aoaCleaned = removeFullyEmptyRows(aoaOriginal, usedMaxCol0);
      cleanedAoABySheet[sheetName] = aoaCleaned;
      if (cfg.returnCleanedWorkbook) {
        wb.Sheets[sheetName] = XLSX.utils.aoa_to_sheet(aoaCleaned);
      }
    } else {
      cleanedAoABySheet[sheetName] = aoaOriginal;
    }
  }

  // Build table output
  const issueTable: any[][] = [
    ["Client", "FTE", "Missing Column", "Sheet", "Period", "Cell"],
  ];
  for (const it of issues) {
    issueTable.push([it.client, it.fte, it.columnDisplayName, it.sheet, it.period, it.cellAddress]);
  }

  const result: ValidationResult = {
    errors,
    issues,
    issueTable,
    cleanedAoABySheet,
  };

  if (cfg.returnCleanedWorkbook) {
    result.cleanedWorkbook = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  }

  return result;
}

// Explicit alias for routing
export const validateGMWorkbook = validateWorkbookAllColumns;

/* =========================
   Helpers
   ========================= */

function toStr(x: any): string {
  return (x === null || x === undefined) ? "" : String(x).trim();
}

function isEmpty(val: unknown): boolean {
  if (val === null || val === undefined) return true;
  if (typeof val === "string" && val.trim() === "") return true;
  if (typeof val === "number" && Number.isNaN(val)) return true;
  return false;
}

function isSheetCompletelyEmpty(aoa: any[][]): boolean {
  for (const row of aoa) {
    if (!row) continue;
    for (const cell of row) {
      if (!isEmpty(cell)) return false;
    }
  }
  return true;
}

function rowHasAnyValue(row: any[], maxCol: number): boolean {
  for (let c = 0; c <= maxCol; c++) {
    if (!isEmpty(row?.[c])) return true;
  }
  return false;
}

function isRowCompletelyEmpty(row: any[], maxCol: number): boolean {
  for (let c = 0; c <= maxCol; c++) {
    if (!isEmpty(row?.[c])) return false;
  }
  return true;
}

function isOnlyFirstCellFilled(row: any[], maxCol: number): boolean {
  for (let c = 1; c <= maxCol; c++) {
    if (!isEmpty(row?.[c])) return false;
  }
  return true;
}

function removeFullyEmptyRows(aoa: any[][], maxCol: number): any[][] {
  return aoa.filter((row) => !isRowCompletelyEmpty(row ?? [], maxCol));
}

function findMetricHeaderRowIndex(aoa: any[][]): number {
  for (let r = 0; r < Math.min(80, aoa.length); r++) {
    const row = aoa[r] ?? [];
    const hasFteB = row.some(v => toStr(v) === "FTE B");
    const hasSaveLoss = row.some(v => toStr(v) === "Save / (Loss)");
    if (hasFteB && hasSaveLoss) return r;
  }
  return -1;
}

function findRowIndexByFirstCell(aoa: any[][], label: string): number {
  for (let r = 0; r < aoa.length; r++) {
    if (toStr(aoa[r]?.[0]) === label) return r;
  }
  return -1;
}

function findRowIndexByFirstCellAfter(aoa: any[][], label: string, startRow: number): number {
  for (let r = startRow; r < aoa.length; r++) {
    if (toStr(aoa[r]?.[0]) === label) return r;
  }
  return -1;
}

function findBlockStarts(headerRow: any[], startLabel: string): number[] {
  const out: number[] = [];
  for (let c = 0; c < headerRow.length; c++) {
    if (toStr(headerRow[c]) === startLabel) out.push(c);
  }
  return out;
}

function inferUsedMaxColFromHeader(headerRow: any[], fallback: number): number {
  for (let c = headerRow.length - 1; c >= 0; c--) {
    if (toStr(headerRow[c]) !== "") return c;
  }
  return fallback;
}

function formatPeriod(v: any): string {
  if (!v) return "";
  if (v instanceof Date && !isNaN(v.getTime())) {
    const yyyy = v.getFullYear();
    const mm = String(v.getMonth() + 1).padStart(2, "0");
    const dd = String(v.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  return toStr(v);
}

type ColumnMeta = {
  period: string;
  metric: string;
  display: string;
  skip: boolean;
};

function buildColumnMeta(params: {
  aoa: any[][];
  headerIdx: number;
  periodRowIndex: number;
  usedMaxCol0Based: number;
  blockStarts: number[];
  skipMetricLabels: string[];
}): Record<number, ColumnMeta> {
  const { aoa, headerIdx, periodRowIndex, usedMaxCol0Based, blockStarts, skipMetricLabels } = params;
  const headerRow = aoa[headerIdx] ?? [];

  const blockForCol = (c: number): number => {
    if (!blockStarts.length) return -1;
    let best = -1;
    for (const s of blockStarts) {
      if (s <= c) best = s;
      else break;
    }
    return best;
  };

  const meta: Record<number, ColumnMeta> = {};

  for (let c = 0; c <= usedMaxCol0Based; c++) {
    const metricRaw = toStr(headerRow[c]);
    const bStart = blockForCol(c);

    const periodVal = (periodRowIndex >= 0)
      ? aoa[periodRowIndex]?.[(bStart >= 0 ? bStart : c)]
      : null;
    const period = formatPeriod(periodVal);

    const metric = metricRaw || `Column ${columnLabel(c)}`;
    const display = metricRaw
      ? (period ? `${period} | ${metricRaw}` : metricRaw)
      : (period ? `${period} | Column ${columnLabel(c)}` : `Column ${columnLabel(c)}`);

    const skip = metricRaw
      ? skipMetricLabels.includes(metricRaw.trim().toLowerCase())
      : false;

    meta[c] = { period, metric, display, skip };
  }

  return meta;
}

function computeFteForColumn(row: any[], blockStarts: number[], col0: number): string {
  if (!blockStarts.length) return "";

  let bStart = -1;
  for (const s of blockStarts) {
    if (s <= col0) bStart = s;
    else break;
  }
  if (bStart < 0) return "";

  const v = row?.[bStart];
  return isEmpty(v) ? "" : toStr(v);
}

function columnLabel(index0: number): string {
  let n = index0 + 1;
  let s = "";
  while (n > 0) {
    const mod = (n - 1) % 26;
    s = String.fromCharCode(65 + mod) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}
